import React, {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { Box, Flex } from 'rebass/styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';

import {
  PhoneNumberChangeAlertWrapper,
  PhoneNumberChangeAlert,
} from './styles';

import {
  Grid,
  Stack,
  Checkbox,
  Button,
  FormLabel,
  ReactSelectWithFlags,
  InputMask,
  StyledFormFeedback,
} from 'ui/components';
import {
  FormInput,
  FormSelect,
  FormGroup,
  Card,
  Loader,
  FormReactSelect,
} from 'ui/patterns';

import {
  useGetTwilioPhoneTypes,
  useGetTwilioBranchOutGroup,
  useGetTwilioRoutingSubGroup,
  useGetTwilioInfo,
  useGetTwilioCountryCodes,
  useSaveTwilioInfo,
  useGetAccounts,
} from 'hooks';
import usePrompt from 'hooks/usePrompt';

import Icons from 'assets/icons';

import {
  optionList,
  getCountryObj,
  formatData,
  isCountryCodeInUSFormat,
  countryOptions,
  isPhnIsInvalidOrNot,
  hasUsFormat,
  isAllowedShortCode,
} from 'utils/reactCountrySelectHelper';
import { showErrorToast, showSuccessToast } from 'utils/showToast';

import { CountryType } from 'types/countryType';
import { TwilioPhone, TwilioContactNumbers } from './telephonyTypes';
import { phoneTypeNameForShortCode } from 'utils/constants/Telephony/telephonyConstants';

function useHelperHook(id: number) {
  const { data: branchOutData, isLoading: l1 } = useGetTwilioBranchOutGroup();
  const { data: routingSubGroup, isLoading: l2 } =
    useGetTwilioRoutingSubGroup();
  const { data: countryCodes, isLoading: l3 } = useGetTwilioCountryCodes();
  const {
    data: twilioInfo,
    isLoading: l4,
    refetch: twilioInfoRefetch,
  } = useGetTwilioInfo(id);
  const { data: phoneTypes, isLoading: l5 } = useGetTwilioPhoneTypes();
  return {
    branchOutData,
    routingSubGroup,
    countryCodes,
    twilioInfo,
    phoneTypes,
    twilioInfoRefetch,
    isLoading: l1 || l2 || l3 || l4 || l5,
  };
}

function Telephony(): ReactElement {
  const [pageIsDirty, setPageIsDirty] = useState<boolean>(false);
  const [twilioData, setTwilioData] = useState<TwilioContactNumbers>([]);
  const [initialPhnDetails, setInitialPhnDetails] = useState<any>({});
  const [showPopover, setShowPopover] = useState<any>({});

  const [page, setPage] = useState(0);
  const [keywordSearch, setKeywordSearch] = useState(''); // keyword search
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState({ sortKey: 'Proteus Account ID', sortOrder: 'asc' });

  const popover: any = useRef();
  const history = useHistory<{ ID: number }>();
  const { accountId } = useParams<{
    accountId: string;
    tabId?: string;
  }>();

  let permissions: any = [];
  const permissionSet = localStorage.getItem('userPermissions');
  if (permissionSet) {
    permissions = JSON.parse(permissionSet);
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    errors,
    setValue,
    trigger,
    formState: { isDirty },
  } = useForm<TwilioPhone>({
    mode: 'onBlur',
  });

  const {
    branchOutData,
    routingSubGroup,
    countryCodes,
    twilioInfo,
    phoneTypes,
    isLoading,
    twilioInfoRefetch,
  } = useHelperHook(parseInt(accountId));

  //watch values for telephony fields
  const Phone: any = watch('TwilioContactNumbers');
  const TwilioBranchOutConditionsId = watch('TwilioBranchOutConditionsId');
  const TwilioWorkFlowId = watch('TwilioWorkFlowId');
  const EnableRecording = watch('EnableRecording');
  const TwilioFlexPriority = watch('TwilioFlexPriority');
  const TwilioPAK = watch('TwilioPAK');
  const RouteToAccountId = watch('RouteToAccountId');


  const { fields, append, remove } = useFieldArray({
    control,
    name: 'TwilioContactNumbers',
  });

  const [routeToAccountIdInputValue, setInputValue] = useState('');
  const [firstRender, setFirstRender] = useState(true);

  const {
    data: accountList,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAccounts(
    "Live",
    keywordSearch,
    columnsSearch,
    currentSortItem,
  );

  const { mutate: saveAccountInfo, isLoading: isSaveAccountInfoLoading } =
    useSaveTwilioInfo();

  // initial data binding
  useEffect(() => {
    if (!isLoading && twilioInfo) {
      let tempTwilio = { ...twilioInfo };
      if (twilioInfo?.TwilioContactNumbers?.length === 0) {
        tempTwilio = {
          ...tempTwilio,
          TwilioContactNumbers: [
            {
              PhoneNumber: '',
              PhoneTypeId: '',
              CountryId: 1,
              Id: 0,
              Name: 'Office',
            },
          ],
        };
      }
      setTwilioData([]);

      if (firstRender) {
        setColumnsSearch({ ['Proteus Account ID']: tempTwilio?.RouteToAccountId });
        setFirstRender(false);
      }

      const phnDetails = tempTwilio?.TwilioContactNumbers?.map((item: any) => ({
        phnType: item?.PhoneTypeId,
        phnNumber: item?.PhoneNumber,
        countryId: item?.CountryId,
        id: item?.Id,
        name: item?.Name,
      }));
      setInitialPhnDetails(phnDetails);
      reset({ ...tempTwilio });
    }
  }, [reset, isLoading, twilioInfo, setValue]);

  const accountsData = useMemo(() => {
    if (accountList) {
      const flatAccounts = accountList.pages
        .map((dataItem: any) => dataItem.Data)
        .flat();

      return flatAccounts.map((account: any) => ({
        value: account?.Id,
        label: account?.AccountNameAndNumberWithId,
      }));
    }
    return [];
  }, [accountList]);

  useEffect(() => {
    if (isDirty) setPageIsDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    document.body.addEventListener('click', onClickHandler);
    return () => {
      document.body.removeEventListener('click', onClickHandler);
    };
  }, []);

  const countryCode = useMemo(() => {
    if (countryCodes) {
      return countryOptions(countryCodes?.Data);
    }
  }, [countryCodes]);

  //branch out checkbox along with value and description
  const telephonyDes = useMemo(() => {
    if (branchOutData) {
      const val = branchOutData?.map((item: any) => {
        const returnValue: any = {};
        returnValue.label = (
          <div className="label">
            <span>{item.name}</span>
            <p> {item?.description}</p>
          </div>
        );
        returnValue.value = item?.id;
        return returnValue;
      });
      return val;
    }
  }, [branchOutData]);

  usePrompt(
    pageIsDirty,
    'Are you sure you want to leave? Changes that you made may not be saved.',
  );

  //Set mask pattern for input-mask based on country code

  function maskPattern(countryId: number | string, phoneTypeName: string | undefined) {
    const countryCode = countryCodes?.Data?.find(
      (item: CountryType) => item?.Id === countryId,
    )?.ISDCode;
    if (phoneTypeName === phoneTypeNameForShortCode) {
      return '999999';
    }
    else if (countryCode === '+1') {
      return '999-999-9999';
    }
    else return '999999999999999';
  }

  //remove phone number
  const handleRemove = (index: any) => {
    const tempCount: Array<{
      PhoneNumber: number;
      PhoneTypeId: string;
      CountryId: number;
      Id: number;
      Name: string;
    }> = [...Phone];
    remove(index);
    const val = tempCount.splice(index, 1);
    reset({
      TwilioBranchOutConditionsId: TwilioBranchOutConditionsId,
      TwilioWorkFlowId: TwilioWorkFlowId,
      EnableRecording: EnableRecording,
      TwilioContactNumbers: [...tempCount],
      TwilioFlexPriority: TwilioFlexPriority,
      TwilioPAK: TwilioPAK,
      RouteToAccountId: RouteToAccountId,
    });
    setTwilioData([...twilioData, ...val]);
  };

  //add new phn details
  const handleAppendEntry = () => {
    const val = [];
    val.push({
      PhoneNumber: '',
      PhoneTypeId: '',
      CountryId: 1,
      IsDeleted: false,
      Id: 0,
      Name: 'Office',
    });
    append({
      PhoneNumber: '',
      PhoneTypeId: '',
      CountryId: 1,
      Id: 0,
      Name: 'Office',
    });

    const tempPhone: any = watch('TwilioContactNumbers');
    reset({
      TwilioBranchOutConditionsId: TwilioBranchOutConditionsId,
      TwilioWorkFlowId: TwilioWorkFlowId,
      EnableRecording: EnableRecording,
      TwilioContactNumbers: [...tempPhone, ...val],
      TwilioFlexPriority: TwilioFlexPriority,
      TwilioPAK: TwilioPAK,
      RouteToAccountId: RouteToAccountId,
    });

    setPageIsDirty(true);
  };

  const onSubmit = (data: any) => {
    //foramtting data
    const formattedData = formatData(
      { ...data },
      accountId,
      twilioData,
      countryCodes?.Data,
      initialPhnDetails,
    );

    saveAccountInfo(
      {
        ...formattedData,
        AccountId: accountId ? parseInt(accountId) : 0,
      },
      {
        onSuccess: (mutationResult: any, data: any) => {
          if (data?.AccountId && mutationResult.Data.AccountId) {
            twilioInfoRefetch();
            showSuccessToast({
              message: 'Your changes were successfully saved.',
              autoClose: 3000,
            });
          }
          setPageIsDirty(false);
        },
        onError: () => {
          showErrorToast({
            message: `${'"An error occured while attempting to save your modifications.Please try again.'}`,
          });
        },
      },
    );
  };

  function onClickHandler(event: any) {
    const includesPopoverElement = event
      .composedPath()
      .includes(popover?.current);
    if (popover?.current && !includesPopoverElement) {
      const newObject: any = {};
      Object.entries(showPopover).forEach(([key]) => {
        newObject[key] = false;
      });
      setShowPopover({ ...newObject });
    }
  }

  return (
    <>
      <Flex
        as="form"
        flexDirection="column"
        height="100%"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          style={{
            flex: '1 1 auto',
            flexGrow: 1,
            overflow: 'auto',
            height: '1px',
          }}
        >
          <Card px={4} py={8}>
            <Box width="max(350px, 60%)">
              <Box
                style={{
                  display: 'grid',
                  gridGap: 3,
                  gridTemplateColumns: '1fr 1fr 2fr',
                  alignItems: 'end',
                  position: 'relative',
                }}
              >
                <FormLabel>Phone type</FormLabel>
                <FormLabel>Name</FormLabel>
                <FormLabel>Phone number</FormLabel>
              </Box>
              <Stack
                direction="y"
                gap={2}
                style={{ position: 'relative', zIndex: 3 }}
              >
                {fields?.map((item: any, index: number) => {
                  return (
                    <Box
                      key={`${item.id}`}
                      sx={{
                        display: 'grid',
                        gridGap: 1,
                        gridTemplateColumns: '1fr 1fr 2fr',
                        alignItems: 'start',
                        position: 'relative',
                      }}
                    >
                      <FormInput
                        display="none"
                        type="text"
                        register={register}
                        name={`TwilioContactNumbers.${index}.Id`}
                        key={`${item.id}${index}${item?.name}`}
                      />
                      <Controller
                        render={(props: {
                          onChange: (value: number) => void;
                          value: number;
                        }) => {
                          return (
                            <FormSelect
                              value={props.value}
                              options={phoneTypes}
                              onChange={(e) => {
                                const newPhoneTypeId = parseInt(e.currentTarget.value, 10);
                                props.onChange(newPhoneTypeId);
                                if (phoneTypes?.find((item) => item?.id === newPhoneTypeId)?.name === phoneTypeNameForShortCode) {
                                  const phoneNumber = Phone[index]?.PhoneNumber;
                                  if (isAllowedShortCode(phoneNumber)) {
                                    setValue(`TwilioContactNumbers.${index}.PhoneNumber`, '');
                                  }
                                }
                                setTimeout(() => {
                                  trigger(`TwilioContactNumbers.${index}.PhoneNumber`);
                                }, 0);
                              }}
                              disabled={!permissions?.isTelephonyAdmin}
                            />
                          );
                        }}
                        defaultValue={''}
                        name={`TwilioContactNumbers.${index}.PhoneTypeId`}
                        control={control}
                      />

                      <div className="pr-masked-input-wrapper">
                        <Controller
                          name={`TwilioContactNumbers.${index}.Name`}
                          control={control}
                          rules={{
                            required: "Name is required",
                            maxLength: {
                              value: 100,
                              message: "Name cannot exceed 100 characters",
                            },
                          }}
                          render={({ field }: any) => (
                            <div>
                              <FormInput
                                name={`TwilioContactNumbers.${index}.Name`}
                                type="text"
                                value={watch(`TwilioContactNumbers.${index}.Name`)}
                                onTextChange={(val) =>
                                  setValue(`TwilioContactNumbers.${index}.Name`, val, {
                                    shouldValidate: true,
                                  })
                                }
                                disabled={!permissions?.isTelephonyAdmin}
                                register={register}
                              />
                              {errors?.TwilioContactNumbers?.[index]?.Name && <span><StyledFormFeedback validation="error">{errors?.TwilioContactNumbers?.[index]?.Name?.message}</StyledFormFeedback></span>}
                            </div>

                          )}
                        />
                      </div>

                      <Flex style={{ gap: '0.25rem' }}>
                        <div
                          className="pr-ReactSelectWithFlags"
                          style={{
                            position: 'relative',
                            alignSelf: 'flex-start',
                          }}
                        >
                          <Controller
                            render={(props: {
                              onChange: (value: number) => void;
                              value: number;
                            }) => {
                              return phoneTypes?.find((item) => item?.id === Phone[index]?.PhoneTypeId)?.name !== phoneTypeNameForShortCode ? (
                                <ReactSelectWithFlags
                                  value={getCountryObj(
                                    props.value,
                                    countryCodes?.Data,
                                  )}
                                  options={optionList(countryCodes?.Data)}
                                  onChange={(e: any) => {
                                    const val = isCountryCodeInUSFormat(
                                      Phone[index]?.CountryId,
                                      e?.value,
                                      countryCode,
                                    );
                                    if (val)
                                      setShowPopover({
                                        ...showPopover,
                                        [`${index}`]: true,
                                      });
                                    else
                                      setShowPopover({
                                        ...showPopover,
                                        [`${index}`]: false,
                                      });
                                    props.onChange(e?.value);
                                    setTimeout(() => {
                                      trigger(`TwilioContactNumbers.${index}.PhoneNumber`)
                                    }, 0)
                                  }}
                                  key={`${index}${item?.id}`}
                                  isDisabled={!permissions?.isTelephonyAdmin} //disabling the fields for those are not telephony admin.
                                />
                              ) : <ReactSelectWithFlags
                                value={null}
                                options={[]}
                                isDisabled={true}
                                placeholder=""
                                key={`${index}${item?.id}-disabled`}
                              />;
                            }}
                            name={`TwilioContactNumbers.${index}.CountryId`}
                            control={control}
                          />
                          {showPopover[`${index}`] === true && (
                            <div ref={popover}>
                              <PhoneNumberChangeAlertWrapper
                                hidden={!showPopover[index]}
                                className={
                                  index === 0
                                    ? 'PhoneNumberChangeAlertWrapper--firstchild'
                                    : ''
                                }
                              >
                                <PhoneNumberChangeAlert>
                                  As you have switched to +1 country code, the
                                  number you have entered initially has been
                                  converted to xxx-xxx-xxxx format.
                                  <Button
                                    size="sm"
                                    className="pr-alert-close"
                                    onClick={() => {
                                      setShowPopover({
                                        ...showPopover,
                                        [`${index}`]: false,
                                      });
                                    }}
                                  >
                                    <Icons.X />
                                  </Button>
                                </PhoneNumberChangeAlert>
                              </PhoneNumberChangeAlertWrapper>
                            </div>
                          )}
                        </div>
                        <div className="pr-masked-input-wrapper">
                          <Controller
                            render={(props: {
                              onChange: (value: number) => void;
                              onBlur: (value: any) => void;
                              value: number;
                            }) => {
                              return (
                                <InputMask
                                  id="phn-number"
                                  pattern={maskPattern(Phone[index]?.CountryId, phoneTypes?.find((item) => item?.id === Phone[index]?.PhoneTypeId)?.name)}
                                  value={props.value}
                                  phoneTypeName={phoneTypes?.find((item) => item?.id === Phone[index]?.PhoneTypeId)?.name}
                                  onChange={(e: any) => {
                                    props.onChange(e?.currentTarget?.value);
                                  }}
                                  onBlur={(e: any) => {
                                    props.onBlur(e);
                                    setTimeout(() => {
                                      trigger(`TwilioContactNumbers.${index}.PhoneNumber`);
                                    }, 0);
                                  }}
                                  onValidChange={() => {
                                    trigger(`TwilioContactNumbers.${index}.PhoneNumber`);
                                  }}
                                  className="pr-masked-input"
                                  feedback={
                                    errors?.TwilioContactNumbers &&
                                    errors?.TwilioContactNumbers[index]
                                      ?.PhoneNumber?.message
                                  }
                                  disabled={!permissions?.isTelephonyAdmin}
                                />
                              );
                            }}
                            rules={{
                              validate: (value: any) => {
                                if (phoneTypes?.find((item) => item?.id === Phone[index]?.PhoneTypeId)?.name === phoneTypeNameForShortCode) {
                                  if (!isAllowedShortCode(value, phoneTypes?.find((item) => item?.id === Phone[index]?.PhoneTypeId)?.name))
                                    return 'Please enter a short code with 5 or 6 digits that does not start with 1.';
                                  return true;
                                }
                                else if (
                                  isPhnIsInvalidOrNot(
                                    value,
                                    Phone[index]?.CountryId,
                                    countryCode,
                                  ) &&
                                  hasUsFormat(
                                    countryCode,
                                    Phone[index]?.CountryId,
                                  )
                                ) {
                                  return 'Please enter a valid phone number (e.g., xxx-xxx-xxxx)';
                                } else if (
                                  hasUsFormat(
                                    countryCode,
                                    Phone[index]?.CountryId,
                                  ) === false &&
                                  isPhnIsInvalidOrNot(
                                    value,
                                    Phone[index]?.CountryId,
                                    countryCode,
                                  )
                                ) {
                                  return 'Please enter a valid phone number between 8 and 15 digits';
                                }
                                return true;
                              },
                            }}
                            defaultValue={''}
                            name={`TwilioContactNumbers.${index}.PhoneNumber`}
                            control={control}
                          />
                        </div>
                      </Flex>
                      {fields?.length > 1 && permissions?.isTelephonyAdmin ? (
                        <Button
                          variant="ghostDanger"
                          style={{
                            height: '35px',
                            position: 'absolute',
                            top: 0,
                            right: '0',
                            transform: 'translateX(calc(100% + 0.5rem))',
                          }}
                          iconBefore={<Icons.X title="Remove phone number" />}
                          title="Remove"
                          onClick={() => {
                            handleRemove(index);
                          }}
                        />
                      ) : null}
                    </Box>
                  );
                })}
                {permissions?.isTelephonyAdmin && (
                  <Button
                    variant="link"
                    iconBefore={<Icons.Add />}
                    title="Add new number"
                    size="sm"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                      handleAppendEntry();
                    }}
                  >
                    Add new number
                  </Button>
                )}
              </Stack>
              <Grid
                minColWidth="350px"
                marginTop={4}
                gap={3}
                style={{ position: 'relative', zIndex: 2 }}
              >
                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    const value = routingSubGroup?.filter(
                      item => item.value === props.value,
                    );
                    const selected = {
                      label: value && value[0]?.label,
                      value: value && value[0]?.value,
                    };

                    return (
                      <div
                        className="pr-select--question-type"
                        style={{ zIndex: 100 }}
                      >
                        <FormReactSelect
                          value={
                            selected.label === undefined
                              ? { label: 'Select one', value: -1 }
                              : selected
                          }
                          label="Routing Group"
                          options={routingSubGroup}
                          onChange={(e: any) => {
                            props.onChange(parseInt(e.value, 10));
                          }}
                          disabled={!permissions?.isTelephonyAdmin}
                        />
                      </div>
                    );
                  }}
                  defaultValue={''}
                  name="TwilioWorkFlowId"
                  control={control}
                />

                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    const value = branchOutData?.filter(
                      item => item.id === props.value,
                    );
                    const selected = {
                      label: value && value[0]?.name,
                      value: value && value[0]?.id,
                    };
                    return (
                      <FormReactSelect
                        value={
                          selected.label === undefined && branchOutData
                            ? {
                              label: branchOutData?.find(
                                item => item?.id === 1,
                              )?.name,
                              value: branchOutData?.find(
                                item => item?.id === 1,
                              )?.id,
                            }
                            : selected
                        }
                        label="Branch-out Group"
                        options={telephonyDes}
                        onChange={(e: any) => {
                          props.onChange(parseInt(e.value, 10));
                        }}
                        disabled={!permissions?.isTelephonyAdmin}
                      />
                    );
                  }}
                  defaultValue={''}
                  name="TwilioBranchOutConditionsId"
                  control={control}
                />
              </Grid>

              <FormGroup
                pt={1}
                style={{
                  position: 'relative',
                  zIndex: 1,
                }}
                mt={4}
              >
                <Stack direction="x" gap={4} alignItems="center">
                  <FormInput
                    name="TwilioFlexPriority"
                    style={{ flexGrow: 1 }}
                    register={register}
                    label="Twilio Flex Priority"
                    type="number"
                    minValue={0}
                    maxLength={2}
                    maxValue={99}
                    disabled={!permissions?.isTelephonyAdmin}
                    validation="info"
                    feedback="Please enter a number between 0 and 99."
                  />
                  <Checkbox
                    register={register}
                    label="Call Recording"
                    name="EnableRecording"
                    disabled={!permissions?.isTelephonyAdmin}
                    marginBottom={1}
                  />
                  <Checkbox
                    register={register}
                    label="Press Any Key to Answer"
                    name="TwilioPAK"
                    disabled={!permissions?.isTelephonyAdmin}
                    marginBottom={1}
                  />
                </Stack>

                <Controller
                  name="RouteToAccountId"
                  control={control}
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number | null;
                  }) => {
                    const handleInputChange = (inputValue: string, action: any) => {
                      // The condition is added to avoid setting the keyword search an option is selected
                      if (inputValue !== '' && action.action !== 'menu-close') {
                        setKeywordSearch(inputValue);
                        setPage(0);
                        setColumnsSearch({});
                      }
                    };

                    const value = accountsData?.filter(
                      item => item.value === props.value,
                    );
                    const selected = {
                      label: value && value[0]?.label,
                      value: value && value[0]?.value,
                    };

                    return (
                      <div
                        className="pr-select--question-type"
                        style={{ zIndex: 200 }}
                      >
                        <FormReactSelect
                          isSearchable={true}
                          inputValue={routeToAccountIdInputValue}
                          isClearable={true}
                          value={
                            selected.value === undefined
                              ? { label: 'Select one', value: -1 }
                              : selected
                          }
                          label="Route to Proteus Account ID"
                          options={accountsData}
                          onChange={(e: any, action: any) => {
                            // If the action is 'clear', reset accountsData with keyword empty and page 0
                            if (action.action === 'clear') {
                              setKeywordSearch('');
                              setPage(0);
                              setColumnsSearch({});
                              setInputValue('');
                            }
                            props?.onChange(parseInt(e?.value, 10));
                          }}
                          onInputChange={(newValue: string, action: any) => {
                            setInputValue(newValue);
                            handleInputChange(newValue, action);
                          }}
                          onBlur={() => {
                            if (selected.value === undefined) {
                              setKeywordSearch('');
                              setPage(0);
                              setInputValue('');
                            }
                          }}
                          disabled={!permissions?.isTelephonyAdmin}
                          menuPlacement='bottom'
                        />
                      </div>
                    );
                  }} />

              </FormGroup>
            </Box>
          </Card>
        </Box>

        {permissions?.isTelephonyAdmin && (
          <Flex justifyContent="flex-end" mt={3}>
            <Stack direction="x" gap={2}>
              <Button
                variant="secondary"
                onClick={() => {
                  reset();
                  history.push('/home/accounts');
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={Object.keys(errors).length > 0 ? true : false}>
                Save
              </Button>
            </Stack>
          </Flex>
        )}
      </Flex>
      {isLoading || isSaveAccountInfoLoading ? <Loader /> : null}
    </>
  );
}

export default Telephony;
